import { Button } from "@/components/ui/button";
import { CalendlyExpert } from "@/features/experts/types/CalendlyExpert";
import { useUserStore } from "@/utils/stores/useUserStore";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useCalendlyUrl } from "../hooks/useCalendlyUrl";
import { useExpertBooking } from "../hooks/useExpertBooking";
import { formatDateToYYYYMMDD } from "../utils/dateFormatters";

interface ExpertBookingCalendarProps {
    expert: CalendlyExpert;
    onBookClick: (expert: CalendlyExpert) => void;
}

export const ExpertBookingCalendar: React.FC<ExpertBookingCalendarProps> = ({
    expert,
    onBookClick,
}) => {
    const { userData } = useUserStore();
    const {
        currentMonth,
        monthAvailabilities,
        isLoadingMonth,
        selectedDate,
        selectedSlot,
        showCalendlyWidget,
        getDaysInMonth,
        isToday,
        isPastDate,
        canNavigateToPreviousMonth,
        changeMonth,
        handleDayClick,
        handleSlotSelect,
        handleBook,
        handleCalendlyClose,
        formatTimeSlot,
    } = useExpertBooking({ expert, onBookClick });

    const { fullUrl } = useCalendlyUrl({
        expert,
        selectedDate,
        selectedSlot,
        userData: {
            email: userData?.email || '',
            name: userData?.name || '',
            familyName: userData?.familyName || '',
            phoneNumber: userData?.phoneNumber || '',
        }
    });

    console.log(fullUrl)

    return (
        <div className="w-full border-t border-solid border-border p-6">
            {!showCalendlyWidget ? (
                <div className="flex flex-col gap-4">
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => changeMonth(-1)}
                            className="normal-case hover:bg-gray-100"
                            disabled={isLoadingMonth || !canNavigateToPreviousMonth()}
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            <span className="hidden md:block">Mese precedente</span>
                        </Button>
                        <h3 className="text-sm font-semibold md:text-lg">
                            {currentMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => changeMonth(1)}
                            className="normal-case hover:bg-gray-100"
                            disabled={isLoadingMonth}
                        >
                            <span className="hidden md:block">Mese successivo</span>
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="relative">
                        {isLoadingMonth && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-50">
                                <div className="animate-spin rounded-full h-12 w-12 border-solid border-4 border-primary border-t-transparent"></div>
                            </div>
                        )}
                        <div className="grid grid-cols-7 gap-2 mb-4">
                            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                                <div key={day} className="text-center font-medium text-sm text-gray-500">
                                    {day}
                                </div>
                            ))}
                            {getDaysInMonth(currentMonth).map((date, index) => {
                                if (!date) {
                                    return <div key={`empty-${index}`} className="p-2" />;
                                }

                                const dateStr = formatDateToYYYYMMDD(date);
                                const hasAvailability = monthAvailabilities[dateStr]?.length > 0;
                                const isPast = isPastDate(date);
                                const isSelected = selectedDate === dateStr;

                                return (
                                    <button
                                        key={dateStr}
                                        onClick={() => handleDayClick(date, hasAvailability)}
                                        className={`p-2 rounded-md text-center relative ${isToday(date)
                                                ? 'ring-2 ring-primary ring-offset-2'
                                                : isPast
                                                    ? 'bg-gray-50 text-gray-400 opacity-50'
                                                    : hasAvailability
                                                        ? isSelected
                                                            ? 'bg-primary/10 hover:bg-primary/20 text-primary border-2 border-primary'
                                                            : 'bg-green-50 hover:bg-green-100 text-green-700 border-2 border-primary'
                                                        : 'bg-gray-50 text-gray-400'
                                            }`}
                                        disabled={isLoadingMonth || isPast || !hasAvailability}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Time Slots Selection */}
                    {selectedDate && monthAvailabilities[selectedDate] && (
                        <div className="mt-4 p-4 border border-border rounded-lg">
                            <h4 className="text-lg font-semibold mb-4">
                                {new Date(selectedDate).toLocaleDateString('it-IT', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long'
                                })}
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {monthAvailabilities[selectedDate].map((slot, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSlotSelect(slot)}
                                        className={`p-3 rounded-md text-center ${selectedSlot === slot
                                                ? 'bg-primary text-white'
                                                : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                    >
                                        {formatTimeSlot(slot)}
                                    </button>
                                ))}
                            </div>
                            {selectedSlot && (
                                <div className="mt-4 flex justify-end">
                                    <Button
                                        onClick={handleBook}
                                        className="bg-primary text-white hover:bg-primary/90"
                                    >
                                        Continua con Calendly
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="h-[600px]">
                    <iframe
                        src={fullUrl}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="Calendly Scheduling Page"
                    />
                </div>
            )}
        </div>
    );
}; 